import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import SortOrder from '@/constants/SortOrder';
import { formatDateToString } from '@/utils/dates';
import prependPrefix from '@/utils/prependPrefix';
import CommonParams from '../constants/CommonParams';
import type FilterParams from '../types/FilterParams';
import type QueryProviderQuery from '../types/QueryProviderQuery';
import type SortableFields from '../types/SortableFields';

const { ASC, DESC } = SortOrder;
const { PAGE, PAGE_SIZE, SORT_BY, SORT_ORDER } = CommonParams;
const getSearchString = <FiltersType>({
  filterParams,
  filterPrefix,
  paginationPrefix,
  query,
  sortableFields,
  sortingPrefix,
  update,
  urlSearch,
}: {
  filterParams: FilterParams;
  filterPrefix?: string;
  paginationPrefix?: string;
  query: QueryProviderQuery<FiltersType>;
  sortableFields: SortableFields;
  sortingPrefix?: string;
  update: Partial<Record<keyof QueryProviderQuery<FiltersType>, unknown>>;
  urlSearch: URLSearchParams;
}) => {
  Object.entries(update).forEach(([key, value]) => {
    // Don't do anything if we're not watching the param.
    if (!(key in query)) return;

    let prefix;
    switch (key) {
      case PAGE:
      case PAGE_SIZE:
        prefix = paginationPrefix;
        break;
      case SORT_BY:
      case SORT_ORDER:
        prefix = sortingPrefix;
        break;
      default:
        prefix = filterPrefix;
        break;
    }

    const snakeCasedKey = prependPrefix(snakeCase(key), prefix);

    // Remove any null or undefiend values.
    if (value === null || value === undefined || Number.isNaN(value)) {
      urlSearch.delete(snakeCasedKey);
      return;
    }

    // If updating the sortBy parameter check if we're actually toggling
    // sort order, which would occur if the same sortBy parameter is being
    // called. If it's not the same, then set the new sortBy value, and set
    // sortOrder to its default. If we are passing `sortOrder` in the
    // update, that should override this logic.
    if (key === SORT_BY && typeof value === 'string') {
      urlSearch.set(snakeCasedKey, snakeCase(value));
      const newSortOrder = update[SORT_ORDER];
      if (snakeCase(query[SORT_BY] as string) === value) {
        urlSearch.set(
          snakeCase(prependPrefix(SORT_ORDER, sortingPrefix)),
          newSortOrder || query[SORT_ORDER] === ASC ? DESC : ASC,
        );
      } else {
        urlSearch.set(
          snakeCase(prependPrefix(SORT_ORDER, sortingPrefix)),
          (newSortOrder ||
            sortableFields[camelCase(value)]?.defaultValue ||
            ASC) as string,
        );
      }
      return;
    }

    // Stringify the value if it is meant to be JSON
    if (key in filterParams && filterParams[key].type === JSON) {
      urlSearch.set(snakeCasedKey, JSON.stringify(value));
      return;
    }

    // Concatenate array values into a comma-separated string, or remove the
    // parameter if the array is empty.
    if (Array.isArray(value)) {
      if (!value.length) {
        urlSearch.delete(snakeCasedKey);
      } else {
        urlSearch.set(snakeCasedKey, value.join(','));
      }
      return;
    }

    // Convert date objects to YYYY-MM-DD format.
    if (value instanceof Date) {
      urlSearch.set(snakeCasedKey, formatDateToString(value));
      return;
    }

    // If the value is a string, delete the key if it's empty whitespace.
    if (typeof value === 'string' && !value.trim()) {
      urlSearch.delete(snakeCasedKey);
      return;
    }

    // And finally if the value is any other param, we can set it here.
    urlSearch.set(snakeCasedKey, value as string);
  });

  // If we are using pagination and the query has changed, but we haven't
  // explicitly set page, we need to reset pagination.
  if (PAGE in query && !(PAGE in update)) {
    urlSearch.delete(PAGE);
  }

  urlSearch.sort();
  const searchString = urlSearch.toString();
  return searchString ? `?${searchString}` : '';
};

export default getSearchString;
